import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { parseSupabaseError, ERROR_MESSAGES } from "@/lib/error-messages";

interface PatientFilters {
  search?: string;
  clinicId?: string;
  clinicIds?: string[];
  hasActiveCases?: boolean;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
  showAll?: boolean;
  incompleteOnly?: boolean;
  enabled?: boolean;
}

type SearchPatientRow = {
  id: string;
  address?: any;
  alternate_phones?: string[] | null;
  blood_group?: string | null;
  clinic_id: string;
  clinic_name?: string | null;
  country?: string | null;
  created_at: string | null;
  created_by?: string | null;
  current_medications?: any;
  date_of_birth?: string | null;
  email?: string | null;
  email_opted_out?: boolean;
  external_id?: string | null;
  first_name: string;
  gender?: string | null;
  guardian_details?: any;
  known_allergies?: any;
  last_name?: string | null;
  last_visited?: string | null;
  marital_status?: string | null;
  name: string;
  notes?: string | null;
  phone: string;
  reference_details?: string | null;
  referral_source?: string | null;
  referred_by_patient_id?: string | null;
  registration_complete?: boolean;
  registration_number: string;
  relationship?: string | null;
  tenant_id: string;
  total_count?: number;
  total_visits?: number;
};

const searchPatients = async ({
  query,
  limit,
  offset = 0,
  clinicIds,
  incompleteOnly,
  startDate,
  endDate,
}: {
  query: string;
  limit: number;
  offset?: number;
  clinicIds?: string[] | null;
  incompleteOnly?: boolean;
  startDate?: string;
  endDate?: string;
}) => {
  const args: Record<string, unknown> = {
    p_query: query,
    p_limit: limit,
    p_offset: offset,
    p_clinic_ids: clinicIds?.length ? clinicIds : null,
  };

  if (incompleteOnly) args.p_incomplete_only = true;
  if (startDate) args.p_start_date = startDate;
  if (endDate) args.p_end_date = endDate;

  const { data, error } = await (supabase.rpc as any)("search_patients", args);

  if (error) throw error;
  return (data || []) as SearchPatientRow[];
};

export const usePatients = (filters?: PatientFilters) => {
  // Require minimum 3 characters for search-first privacy mode (unless showAll is true)
  const searchTerm = filters?.search || "";
  const showAll = filters?.showAll || false;
  const hasMinimumSearch = searchTerm.length >= 3 || showAll;

  return useQuery({
    queryKey: ["patients", filters],
    enabled: hasMinimumSearch && filters?.enabled !== false,
    queryFn: async () => {
      const page = filters?.page || 1;
      const pageSize = filters?.pageSize || 50;
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      const clinicIds = filters?.clinicId
        ? [filters.clinicId]
        : filters?.clinicIds;

      const data = await searchPatients({
        query: searchTerm.trim(),
        limit: pageSize,
        offset: from,
        clinicIds,
        incompleteOnly: filters?.incompleteOnly,
        startDate: filters?.startDate,
        endDate: filters?.endDate,
      });

      const count = data[0]?.total_count || 0;
      const mappedData = data.map(({ total_count: _totalCount, ...patient }) => patient);

      return { data: mappedData, count };
    },
  });
};

export const usePatientTotalCount = (opts?: { clinicIds?: string[]; enabled?: boolean }) => {
  // Reuse the tenant-scoped search_patients RPC (its count(*) OVER() total_count)
  // instead of an unfiltered count(*) over patients: the direct count evaluates
  // the tenant RLS helpers row-by-row over the whole table and times out on
  // production-sized data, so the header silently showed 0.
  return useQuery({
    queryKey: ["patients-total-count", opts?.clinicIds ?? null],
    enabled: opts?.enabled !== false,
    queryFn: async () => {
      const data = await searchPatients({
        query: "",
        limit: 1,
        offset: 0,
        clinicIds: opts?.clinicIds ?? null,
      });
      return data[0]?.total_count ?? 0;
    },
  });
};

export const usePatient = (patientId?: string) => {
  return useQuery({
    queryKey: ["patient", patientId],
    queryFn: async () => {
      if (!patientId) return null;

      const { data, error } = await supabase
        .from("patients")
        .select("*")
        .eq("id", patientId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!patientId,
  });
};

export const usePatientCases = (patientId?: string) => {
  return useQuery({
    queryKey: ["patient-cases", patientId],
    queryFn: async () => {
      if (!patientId) return [];

      const { data, error } = await supabase
        .from("cases")
        .select(`
          *,
          clinic:clinics(name),
          work_type:work_types(name),
          doctor:profiles!cases_doctor_id_fkey(full_name),
          lab:labs(name),
          patient:patients(name)
        `)
        .eq("patient_id", patientId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      // Map the data to flatten nested objects for CaseTable compatibility
      return data?.map((caseItem: any) => ({
        ...caseItem,
        work_type: caseItem.work_type?.name || null,
        lab_name: caseItem.lab?.name || null,
        patient_name: caseItem.patient?.name || null,
        clinic_name: caseItem.clinic?.name || null,
      })) || [];
    },
    enabled: !!patientId,
  });
};

export const usePatientTasks = (patientId?: string) => {
  return useQuery({
    queryKey: ["patient-tasks", patientId],
    queryFn: async () => {
      if (!patientId) return [];

      const { data, error } = await supabase
        .from("tasks")
        .select(`
          *,
          clinic:clinics(name),
          assigned_to_user:profiles!tasks_assigned_to_fkey(full_name),
          case:cases(case_number)
        `)
        .eq("patient_id", patientId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!patientId,
  });
};

export const usePatientAppointments = (patientId?: string) => {
  return useQuery({
    queryKey: ["patient-appointments", patientId],
    queryFn: async () => {
      if (!patientId) return [];

      const { data, error } = await supabase
        .from("appointments")
        .select(`
          *,
          clinic:clinics(name),
          doctor:profiles!appointments_doctor_id_fkey(full_name),
          operatory:operatories(name),
          appointment_type:appointment_types(name, color)
        `)
        .eq("patient_id", patientId)
        .order("start_time", { ascending: false });

      if (error) throw error;
      
      return data?.map((appointment: any) => ({
        ...appointment,
        clinic_name: appointment.clinic?.name || null,
        doctor_name: appointment.doctor?.full_name || "Not assigned",
        operatory_name: appointment.operatory?.name || null,
        appointment_type_name: appointment.appointment_type?.name || null,
        appointment_type_color: appointment.appointment_type?.color || null,
      })) || [];
    },
    enabled: !!patientId,
  });
};

export const useSearchPatients = (searchTerm: string) => {
  return useQuery({
    queryKey: ["search-patients", searchTerm],
    enabled: searchTerm.length >= 3, // Start searching at 3 digits for phone numbers
    queryFn: async () => {
      const data = await searchPatients({
        query: searchTerm.trim(),
        limit: 10,
        offset: 0,
        clinicIds: null,
      });

      return data.map(({ total_count: _totalCount, ...patient }) => patient);
    },
  });
};

export const useCheckDuplicatePatient = () => {
  return useMutation({
    mutationFn: async ({ name, phone, clinicId, excludeId }: { name: string; phone: string; clinicId?: string; excludeId?: string }) => {
      const { data, error } = await supabase.rpc("check_duplicate_patient", {
        p_name: name,
        p_phone: phone,
        p_clinic_id: clinicId || null,
        p_exclude_id: excludeId || null,
      });

      if (error) throw error;
      return data;
    },
  });
};

export const useCreatePatient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patient: {
      first_name: string;
      last_name: string;
      phone: string;
      registration_number: string;
      email?: string;
      date_of_birth?: string;
      gender?: string;
      notes?: string;
      clinic_id: string;
      blood_group?: string;
      marital_status?: string;
      country?: string;
      referral_source?: string;
      reference_details?: string;
      address?: any;
      guardian_details?: any;
      alternate_phones?: string[];
      registration_complete?: boolean;
      referred_by_patient_id?: string | null;
    }) => {
      // Offline fallback
      if (!navigator.onLine) {
        const { syncManager } = await import("@/lib/sync-manager");
        const queuedId = await syncManager.queueSync("patient", "create", {
          ...patient,
          name: `${patient.first_name} ${patient.last_name}`,
        });
        return {
          id: queuedId,
          first_name: patient.first_name,
          last_name: patient.last_name,
          name: `${patient.first_name} ${patient.last_name}`,
          phone: patient.phone,
          is_queued: true,
        };
      }

      const { data, error } = await supabase
        .from("patients")
        .insert([{ ...patient, name: '' } as any]) // Name will be auto-calculated by database trigger
        .select()
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      queryClient.invalidateQueries({ queryKey: ["search-patients"] });
      if (data?.is_queued) {
        toast.info("Working offline. Patient saved locally and will sync when online.");
      } else {
        toast.success("Patient created successfully");
      }
    },
    onError: (error) => {
      toast.error(`Failed to create patient: ${error.message}`);
    },
  });
};

export const useUpdatePatient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: {
      id: string;
      name?: string;
      first_name?: string;
      last_name?: string;
      title?: string;
      phone?: string;
      alternate_phones?: string[];
      registration_number?: string;
      email?: string;
      date_of_birth?: string;
      gender?: string;
      blood_group?: string;
      marital_status?: string;
      relationship?: string;
      country?: string;
      referral_source?: string;
      reference_details?: string;
      external_id?: string;
      address?: any;
      guardian_details?: any;
      notes?: string;
      registration_complete?: boolean;
      known_allergies?: any;
      current_medications?: any;
    }) => {
      const { data, error } = await supabase
        .from("patients")
        .update(updates)
        .eq("id", id)
        .select()
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      // Use variables.id (input) instead of data.id to avoid null reference when RLS restricts SELECT
      queryClient.invalidateQueries({ queryKey: ["patient", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["search-patients"] });
      toast.success("Patient updated successfully");
    },
    onError: (error) => {
      toast.error(`Failed to update patient: ${error.message}`);
    },
  });
};

export const useDeletePatient = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (patientId: string) => {
      const { error } = await supabase
        .from("patients")
        .delete()
        .eq("id", patientId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      toast.success("Patient deleted successfully");
    },
    onError: (error) => {
      toast.error(`Failed to delete patient: ${error.message}`);
    },
  });
};
